import { useMemo } from 'react';

import { useHasNext } from '@ui/Hooks/useHasNext';
import { TagFlowViewState, TagResultState } from '@ui/Utils/scan/tag/scanTypes';

import { useTagExists } from 'kerberus-components/src/Utils/scan/tag/useTagExists';

import { checkScannedUrl } from './allowedUrls';
import { useTagHasOwner } from './useTagHasOwner';
import { useTagOwnerPermission } from './useTagOwnerPermission';

export function useTagScanFlow(data: TagResultState | null) {
    const hasNext = useHasNext();
    const serial = data?.serial ?? '';
    const shouldCheck = Boolean(serial);

    // Check if scanned URL is allowed
    const { isAllowed, redirectUrl } = checkScannedUrl(data?.raw || '');

    // Get current API host
    const currentApiHost = process.env.EXPO_PUBLIC_RENKI_API_HOST || 'http://api.localhost/v1';

    // Check if scanned URL is from wrong domain
    const wrongDomain = Boolean(isAllowed && data?.raw && !data.raw.startsWith(currentApiHost));

    // Check if tag exists in Renki system
    const {
        exists,
        isLoading: isTagExistsLoading,
        mutate: mutateTagExists,
        tagNotAdded,
    } = useTagExists(shouldCheck ? serial : '');
    const notRenkiTag = Boolean(data && isAllowed === false && !exists);

    // Check if user is owner of the tag
    const {
        isLoading: isOwnerLoading,
        isOwner,
        mutate: mutateOwnerPermission,
    } = useTagOwnerPermission(shouldCheck ? serial : '');

    // Check if tag has any owner
    const {
        hasOwner,
        isLoading: isHasOwnerLoading,
        mutate: mutateTagHasOwner,
    } = useTagHasOwner(shouldCheck ? serial : '');

    // Check if tag is compromised
    const isCompromised = Boolean(exists && isAllowed === false);

    return useMemo(() => {
        // Unknown / Loading state
        if (!data || isTagExistsLoading || isOwnerLoading || isHasOwnerLoading) {
            return {
                mutateOwnerPermission,
                mutateTagExists,
                mutateTagHasOwner,
                redirectUrl: null,
                viewState: TagFlowViewState.Unknown,
                wrongDomain: false,
            };
        }

        let viewState: TagFlowViewState = TagFlowViewState.Unknown;
        // Wrong domain
        if (wrongDomain) {
            viewState = TagFlowViewState.WrongDomain;
        }
        // Not a Renki tag (URL not allowed)
        else if (notRenkiTag) {
            viewState = TagFlowViewState.NotRenki;
        }
        // Compromised
        else if (isCompromised) {
            viewState = TagFlowViewState.Compromised;
        }
        // Not a Renki tag (URL not allowed) and cant be added because no next available
        else if (tagNotAdded && !hasNext) {
            viewState = TagFlowViewState.NotRenki;
        }
        // Tag not added to Renki system (URL not allowed but tag serial in system)
        else if (tagNotAdded && hasNext) {
            viewState = TagFlowViewState.NotAdded;
        }
        // Tag has no owner
        else if (!hasOwner && exists) {
            viewState = TagFlowViewState.NoOwner;
        }
        // Tag exists but user is not owner. Just display link to navigate.
        else if (isAllowed && !isOwner && exists) {
            viewState = TagFlowViewState.Navigate;
        }
        // Tag exists and user is owner. Show owner links and actions.
        else if (isAllowed && isOwner && exists) {
            viewState = TagFlowViewState.TagOwner;
        }
        return {
            mutateOwnerPermission,
            mutateTagExists,
            mutateTagHasOwner,
            redirectUrl,
            serial,
            viewState,
            wrongDomain,
        };
    }, [
        data,
        isTagExistsLoading,
        isOwnerLoading,
        isHasOwnerLoading,
        notRenkiTag,
        isCompromised,
        tagNotAdded,
        hasNext,
        hasOwner,
        isAllowed,
        isOwner,
        exists,
        serial,
        redirectUrl,
        wrongDomain,
        mutateTagExists,
        mutateOwnerPermission,
        mutateTagHasOwner,
    ]);
}
